import { Badge, type BadgeProps } from '@mui/material';
import type { ReactNode } from 'react';

export type StatusBadgeSeverity =
  'success' | 'info' | 'warning' | 'error' | 'neutral';

export type StatusBadgeProps = Omit<
  BadgeProps,
  'badgeContent' | 'children' | 'color'
> & {
  severity?: StatusBadgeSeverity;
  badgeContent?: ReactNode;
  children?: ReactNode;
};

const SEVERITY_STYLES: Record<
  StatusBadgeSeverity,
  { backgroundColor: string; color: string; border?: string }
> = {
  success: {
    backgroundColor: 'var(--color-light-greyish-lime-green)',
    color: 'var(--color-deep-ocean-blue)',
    border: '1px solid var(--color-scifree-teal)',
  },
  info: {
    backgroundColor: 'var(--color-light-greyish-blue)',
    color: 'var(--color-deep-ocean-blue)',
    border: '1px solid var(--color-scifree-teal)',
  },
  warning: {
    backgroundColor: 'var(--color-very-pale-orange)',
    color: 'var(--color-deep-ocean-blue)',
    border: '1px solid var(--color-deep-ocean-blue)',
  },
  error: {
    backgroundColor: 'var(--color-dark-grey-red)',
    color: 'var(--color-white)',
  },
  neutral: {
    backgroundColor: 'var(--color-greyish-blue)',
    color: 'var(--color-deep-ocean-blue)',
    border: '1px solid var(--color-dark-greyish-blue)',
  },
};

export function StatusBadge({
  severity = 'error',
  badgeContent = '!',
  children,
  sx,
  ...rest
}: StatusBadgeProps) {
  const { backgroundColor, color, border } = SEVERITY_STYLES[severity];

  return (
    <Badge
      {...rest}
      badgeContent={badgeContent}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: backgroundColor,
          color: color,
          ...(border ? { border } : null),
        },
        ...sx,
      }}
    >
      {children}
    </Badge>
  );
}
